/* agmf-product-bridge.js — one product-page contract for every AGMF card.
 *
 * Cards must not know which product shell they are sitting beside. Standard
 * Shopify PDPs submit one product form; Gear Builder PDPs add a base plus a
 * loadout through their visible add-all button; EasySuggestions enhances the
 * real button in either shell. This bridge resolves those differences once.
 *
 * The important rule is that addToCart() CLICKS THE REAL ACTIVE BUTTON. That
 * preserves variant selection, builder line-item flattening, EasySuggestions,
 * terms gates, cart caps, gifts, drawer refreshes and their existing tracking.
 * A card must never duplicate those paths with its own /cart/add request.
 *
 * Public contract:
 *   getMode()                  -> "gear-builder" | "standard"
 *   getAddToCartButton()       -> the visible real button, or null
 *   getAddToCartState()        -> normalized label/disabled/button/mode
 *   addToCart()                -> delegates to the real button; true if pressed
 *   getSelectedVariantId()     -> current base/product variant, or null
 *   getDeliveryElement/Text()  -> the page's one delivery answer
 *   getBuyColumn()             -> the product shell's complete buy column
 *   subscribe(fn)              -> source/state changes; returns unsubscribe
 *   registerAdapter(adapter)   -> future product shells without card rewrites
 *
 * Loaded once on every product page from theme.liquid. Kill switch:
 * window.AGMF_PRODUCT_BRIDGE_OFF = true.
 */
(function () {
  'use strict';
  if (window.AGMF_PRODUCT_BRIDGE || window.AGMF_PRODUCT_BRIDGE_OFF) return;

  var GEAR_ATC = '#add-all-btn-2, #add-all-btn, .js-add-all';
  var STANDARD_ATC = 'product-form button[type="submit"], .product-form button[type="submit"], ' +
                     'form[action*="/cart/add"] button[type="submit"], buy-buttons button[type="submit"]';
  var DELIVERY = '.delivery-estimate';
  var STANDARD_BUY_COLUMN = '.product-info';
  var GEAR_BUY_COLUMN = '.agb-main';
  var SECONDARY_ATC = '[data-agmf-buy-tail-atc], [data-agmf-delivery-atc], [data-agmf-sticky-atc]';
  var WATCH = GEAR_ATC + ', ' + STANDARD_ATC + ', ' + DELIVERY + ', ' +
              STANDARD_BUY_COLUMN + ', ' + GEAR_BUY_COLUMN;

  var adapters = [];
  var subscribers = [];
  var observer = null;
  var watching = false;
  var pending = 0;
  var pendingReason = '';

  function isRendered(el) {
    if (!el || !el.getClientRects || !el.getClientRects().length) return false;
    // Secondary AGMF controls may deliberately inherit the real button's
    // classes. Never resolve one of our delegates as the source or a click
    // could recurse back into the bridge when the primary control disappears.
    if (el.matches && el.matches(SECONDARY_ATC)) return false;
    var css = window.getComputedStyle ? window.getComputedStyle(el) : null;
    return !css || (css.display !== 'none' && css.visibility !== 'hidden');
  }

  function firstRendered(selector, root) {
    var candidates = (root || document).querySelectorAll(selector);
    for (var i = 0; i < candidates.length; i++) {
      if (isRendered(candidates[i])) return candidates[i];
    }
    return null;
  }

  function activeAdapter() {
    for (var i = 0; i < adapters.length; i++) {
      try {
        if (adapters[i].matches()) return adapters[i];
      } catch (e) { /* one optional adapter must not break every card */ }
    }
    return null;
  }

  function getMode() {
    var adapter = activeAdapter();
    return adapter ? adapter.id : 'standard';
  }

  function getAddToCartButton() {
    var adapter = activeAdapter();
    if (!adapter || typeof adapter.getAddToCartButton !== 'function') return null;
    try { return adapter.getAddToCartButton(); }
    catch (e) { return null; }
  }

  function getAddToCartState() {
    var button = getAddToCartButton();
    var label = button ? (button.textContent || '').replace(/\s+/g, ' ').trim() : '';
    var disabled = !button || !!button.disabled || button.getAttribute('aria-disabled') === 'true';
    return {
      mode: getMode(),
      button: button,
      label: label,
      disabled: disabled,
      className: button ? String(button.className || '') : ''
    };
  }

  function addToCart() {
    var state = getAddToCartState();
    if (!state.button || state.disabled) return false;
    state.button.click();
    return true;
  }

  function numericId(value) {
    var text = value === null || value === undefined ? '' : String(value).trim();
    return /^\d+$/.test(text) ? Number(text) : null;
  }

  function getSelectedVariantId() {
    // The builder state is the freshest answer after a programmatic colour
    // change; its hidden form input can lag briefly behind it.
    if (getMode() === 'gear-builder') {
      var base = window.baseItem || null;
      var builderId = base && numericId(base.variantId || base.id);
      if (builderId) return builderId;
    }

    var button = getAddToCartButton();
    var form = button && button.form;
    if (!form && button && button.getAttribute('form')) {
      form = document.getElementById(button.getAttribute('form'));
    }
    var input = form && form.querySelector('[name="id"]');
    if (!input) input = document.querySelector('form[action*="/cart/add"] [name="id"], product-form [name="id"]');
    var id = input && numericId(input.value);
    if (id) return id;

    var sticky = document.querySelector('[data-agmf-sticky-variant]');
    return sticky ? numericId(sticky.value || sticky.getAttribute('data-agmf-sticky-variant')) : null;
  }

  function getDeliveryElement() { return document.querySelector(DELIVERY); }

  function getDeliveryText() {
    var source = getDeliveryElement();
    if (!source) return '';
    var message = source.querySelector('.agb-delivery-estimate__msg') || source;
    return (message.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function getBuyColumn() {
    var adapter = activeAdapter();
    if (adapter && typeof adapter.getBuyColumn === 'function') {
      try {
        var column = adapter.getBuyColumn();
        if (column) return column;
      } catch (e) { /* fall through to the standard shell */ }
    }
    return document.querySelector(STANDARD_BUY_COLUMN);
  }

  function registerAdapter(adapter, prepend) {
    if (!adapter || !adapter.id || typeof adapter.matches !== 'function' ||
        typeof adapter.getAddToCartButton !== 'function') return false;
    for (var i = adapters.length - 1; i >= 0; i--) {
      if (adapters[i].id === adapter.id) adapters.splice(i, 1);
    }
    if (prepend === false) adapters.push(adapter);
    else adapters.unshift(adapter);
    schedule('adapter');
    return true;
  }

  function related(node) {
    if (!node) return false;
    if (node.nodeType === 3) node = node.parentElement;
    if (!node || node.nodeType !== 1) return false;
    try {
      return (node.matches && node.matches(WATCH)) ||
             (node.closest && node.closest(WATCH)) ||
             (node.querySelector && !!node.querySelector(WATCH));
    } catch (e) { return false; }
  }

  function mutationsMatter(records) {
    for (var i = 0; i < records.length; i++) {
      var rec = records[i];
      if (related(rec.target)) return true;
      for (var a = 0; a < (rec.addedNodes || []).length; a++) {
        if (related(rec.addedNodes[a])) return true;
      }
      for (var r = 0; r < (rec.removedNodes || []).length; r++) {
        if (related(rec.removedNodes[r])) return true;
      }
    }
    return false;
  }

  function emit(reason) {
    pending = 0;
    var why = reason || pendingReason || 'change';
    pendingReason = '';
    var copy = subscribers.slice();
    for (var i = 0; i < copy.length; i++) {
      try { copy[i](api, why); }
      catch (e) { /* one card cannot break the bridge */ }
    }
    try {
      document.dispatchEvent(new CustomEvent('agmf:product-bridge-change', {
        detail: { mode: getMode(), reason: why }
      }));
    } catch (e) {}
  }

  function schedule(reason) {
    pendingReason = reason || pendingReason || 'change';
    if (pending) return;
    pending = setTimeout(function () { emit(pendingReason); }, 0);
  }

  function startWatching() {
    if (watching) return;
    watching = true;
    document.addEventListener('variant:change', function () { schedule('variant'); });
    document.addEventListener('agb:gs-ready', function () { schedule('gear-config'); });
    window.addEventListener('resize', function () { schedule('viewport'); });

    function observeBody() {
      if (observer || !document.body || !window.MutationObserver) return;
      observer = new MutationObserver(function (records) {
        if (mutationsMatter(records)) schedule('dom');
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'style']
      });
    }

    if (document.body) observeBody();
    else document.addEventListener('DOMContentLoaded', observeBody, { once: true });
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return function () {};
    subscribers.push(fn);
    startWatching();
    try { fn(api, 'subscribe'); } catch (e) {}
    return function () {
      var index = subscribers.indexOf(fn);
      if (index !== -1) subscribers.splice(index, 1);
    };
  }

  // Later registrations go first. Keep the generic standard adapter last.
  registerAdapter({
    id: 'standard',
    matches: function () { return true; },
    getAddToCartButton: function () { return firstRendered(STANDARD_ATC); },
    getBuyColumn: function () { return document.querySelector(STANDARD_BUY_COLUMN); }
  }, false);

  registerAdapter({
    id: 'gear-builder',
    matches: function () {
      return !!(window.AGB_BOOT || document.querySelector(GEAR_ATC));
    },
    getAddToCartButton: function () { return firstRendered(GEAR_ATC); },
    // The builder's product-info contains only the base-item form. Suggestions,
    // accordions and the rest of the right column are its siblings, so the
    // outer builder main is the only honest "end of column" target.
    getBuyColumn: function () {
      return document.querySelector(GEAR_BUY_COLUMN) || document.querySelector(STANDARD_BUY_COLUMN);
    }
  });

  var api = {
    version: 1,
    getMode: getMode,
    getAddToCartButton: getAddToCartButton,
    getAddToCartState: getAddToCartState,
    addToCart: addToCart,
    getSelectedVariantId: getSelectedVariantId,
    getDeliveryElement: getDeliveryElement,
    getDeliveryText: getDeliveryText,
    getBuyColumn: getBuyColumn,
    subscribe: subscribe,
    registerAdapter: registerAdapter,
    refresh: schedule
  };

  window.AGMF_PRODUCT_BRIDGE = api;
})();
